import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectAiProduct } from '@store/ai-product/selectors';
import { useRef, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import ProductImageEditor from '@pages/ai-product/image-editor/ProductImageEditor';
import ProductImageFormDialog from '@pages/ai-product/ProductImageFormDialog';
import {
  processAiProduct,
  renderAiProduct,
  updateAiProduct,
} from '@store/ai-product/actions';
import { actions } from '@store/companies';
import { ROUTES } from '@app/routes/routesUrls';
import useNavigate from '@shared/router/useNavigate';
import Button from '@shared/components/button';
import { SketchPicker } from 'react-color';
import useOnClickOutside from '@shared/hooks';
import Backdrop from '@shared/components/backdrop';
import CircularProgress from '@shared/components/circular-progress';
import PaleteIcon from '@resources/icons/PaleteIcon';
import CreateIcon from '@resources/icons/CreateIcon';
import ScaleIcon from '@resources/icons/ScaleIcon';
import Slider from '@shared/components/slider/Slider';
import LimitBanner from '@shared/components/page-parts/LimitBanner';
import AIProduct from '..';

const useSliderStyles = makeStyles((theme) => ({
  rail: {
    border: `1px solid ${theme.palette.common.divider}`,
  },
  track: {
    background: 'transparent',
  },
  thumb: {
    backgroundColor: '#fff',
    border: '4px solid #334D6E',
    height: '20px',
    width: '20px',
    marginTop: '-10px',
  },
  mark: {
    backgroundColor: '#fff',
    border: '2px solid #334D6E',
    height: '8px',
    width: '8px',
    marginTop: '-5px',
    opacity: 1,
    borderRadius: '50%',
  },
}));

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontSize: '14px',
    padding: '16px',
    width: '100%',
    height: '100%',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    color: '#334D6E',
    fontWeight: 700,
    textDecoration: 'underline',
    '& > div': {
      width: '100%',
      maxWidth: '200px',
    },
    marginBottom: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '20px',
  },
  contentHeader: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '20px',
    fontWeight: 700,
  },
  contentSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  imagesWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  },
  uploadImage: {
    width: '268px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    height: '184px',
    objectFit: 'cover',
    border: '1px solid #e2e2e2',
    borderRadius: '4px',
    flex: '0 0 268px',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    '&.active': {
      border: `1px solid ${theme.palette.common.darkGrey}`,
    },
  },
  imageEditorWrapper: {
    display: 'flex',
    marginTop: '20px',
    gap: '16px',
    flex: '1',
  },
  productImageEditorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '16px',
    flex: '1 1 auto',
    maxWidth: '100%',
  },
  submitButton: {
    alignSelf: 'center',
  },
  imageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    marginTop: '50px',
    gap: '16px',
    '& > button': {
      minWidth: '142px',
      width: '100%',
    },
    '& > button.button__grey_ghost': {
      borderColor: '#192A3E',
      color: '#192A3E',
      '&:disabled': {
        color: '#fff',
        backgroundColor: theme.palette.common.grey,
        borderColor: 'unset',
      },
    },
  },
  toolBarWrapper: {
    display: 'flex',
    gap: '20px',
  },
  formDialog: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  buttonBackground: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    fontWeight: 500,
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '10px',
    lineHeight: 'normal',
    verticalAlign: 'middle',
    alignItems: 'center',
    '& > svg': {
      fontSize: '1.8rem',
    },
    '& > button': {
      maxWidth: 'unset',
    },
  },
  buttonScale: {
    fontSize: '13px',
    color: '#192A3E',
    fontWeight: 500,
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '10px',
    lineHeight: 'normal',
    verticalAlign: 'middle',
    alignItems: 'center',
    '& > svg': {
      fontSize: '1.8rem',
    },
    '& > button': {
      maxWidth: 'unset',
    },
  },
  scaleSliderWrapper: {
    width: '272px',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '0 40px',
    position: 'absolute',
    marginTop: '5px',
    height: '60px',
    borderRadius: '4px',
    border: '1px solid #D8DBE2',
    backgroundColor: '#fff',
    boxShadow: 'rgba(0, 0, 0, 0.15) 0px 0px 0px 1px, rgba(0, 0, 0, 0.15) 0px 8px 16px',

  },
  buttonCreate: {
    fontSize: '13px',
    fontWeight: 500,
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '10px',
    lineHeight: 'normal',
    verticalAlign: 'middle',
    alignItems: 'center',
    '& > svg': {
      fontSize: '1.8rem',
      '& > path': {
        stroke: '#fff',
      },
    },
    '& > button': {
      maxWidth: 'unset',
    },
  },
  colorButton: {
    position: 'relative',
    minWidth: '200px',
    width: '100%',
    '& > button': {
      maxWidth: '100%',
    },
  },
  scaleButton: {
    minWidth: '200px',
    position: 'relative',
    width: '100%',
    '& > button': {
      maxWidth: 'unset',
    },
  },
  btnDisabled: {
    color: '#fff',
    '& > svg path': {
      fill: '#fff',
      stroke: '#fff',
    },
  },
  createButton: {
    minWidth: '200px',
    width: '100%',
    '& > button': {
      maxWidth: 'unset',
    },
  },
  colorPicker: {
    position: 'absolute',
    marginTop: '5px',
    zIndex: 1,
  },
  backdrop: {
    top: 0,
  },
}));

export function SiloStep() {
  const classes = useStyles();
  const sliderClasses = useSliderStyles();
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const product = useSelector(selectAiProduct);
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const containerRef = useRef();
  const stageRef = useRef();
  const [isProductImageOpen, setIsProductImageOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(product?.images[0]);
  const [showColor, setShowColor] = useState(false);
  const [showScaleSlider, setShowScaleSlider] = useState(false);
  const [isColorChanged, setIsColorChanged] = useState(false);
  const { getCurrentSettings } = actions;

  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);

  const isAnyImageShadowing = product.images.some((image) => image.state === 'SHADOWING' || image.state === 'REMOVING_BACKGROUND');

  useOnClickOutside(containerRef, () => {
    setShowColor(false);
    setShowScaleSlider(false);
  });

  const handleCancel = () => {
    setIsProductImageOpen(false);
  };

  const handleSave = () => {
    dispatch(updateAiProduct(product, null, () => {
      const imageIds = product.images
        .map((image) => image.id);
      if (imageIds.length) {
        dispatch(processAiProduct(product.id, {
          tasks: ['SHADOWS'],
          images: imageIds.map((id) => ({ imageId: id })),
          isBeta: false,
        }));
      }
    }));
    setIsProductImageOpen(false);
  };

  const handleColorChange = (value) => {
    dispatch(renderAiProduct({
      ...product,
      images: product.images.map((image) => {
        image.siloBackgroundColor = value.rgb;
        return image;
      }),
    }));
    setIsColorChanged(true);
  };

  const handleScaleChange = (event) => {
    dispatch(renderAiProduct({
      ...product,
      scale: event.value,
    }));
  };

  return (
    <div className={ classes.pageWrapper }>
      <AIProduct />
      <div className={ classes.header }>
        Product Detail Page Content Pack
      </div>
      <LimitBanner show={ isAiProductReadOnly } />
      <div className={ classes.content }>
        <div className={ classes.contentHeader }>
          Step 1. SILO-shots
          <div className={ classes.contentSubHeader }>
            You can change the background colors and scale of the product in the photo.
          </div>
        </div>
        <div ref={ containerRef } className={ classes.toolBarWrapper }>
          <div className={ classes.colorButton }>
            <Button
              color="secondary"
              variant={ isAnyImageShadowing ? '' : 'outlined' }
              onClick={ () => {
                setShowColor(!showColor);
                setShowScaleSlider(false);
              } }
              disabled={ isAnyImageShadowing }
            >
              <div className={ classNames(classes.buttonBackground, { [classes.btnDisabled]: isAnyImageShadowing }) }>
                <PaleteIcon
                  color={
                    isColorChanged
                    // eslint-disable-next-line max-len
                      ? `rgb(${product.images[0]?.siloBackgroundColor?.r},${product.images[0]?.siloBackgroundColor?.g},${product.images[0]?.siloBackgroundColor?.b})`
                      : '#192A3E'
                  }
                />
                Background color
              </div>
            </Button>
            {
                showColor && (
                <SketchPicker
                  className={ classes.colorPicker }
                  color={ product?.images[0].siloBackgroundColor || '#fff' }
                  disableAlpha
                  onChange={ handleColorChange }
                />
                )
            }
          </div>
          <div className={ classes.scaleButton }>
            <Button
              color="secondary"
              variant={ isAnyImageShadowing ? '' : 'outlined' }
              onClick={ () => {
                setShowScaleSlider(!showScaleSlider);
                setShowColor(false);
              } }
              disabled={ isAnyImageShadowing }
            >
              <div className={ classNames(classes.buttonScale, { [classes.btnDisabled]: isAnyImageShadowing }) }>
                <ScaleIcon />
                Scale
              </div>
            </Button>
            { showScaleSlider && (
              <div className={ classes.scaleSliderWrapper }>
                <Slider
                  name="scale-slider"
                  value={ product.scale || 1 }
                  aria-labelledby="scale-slider"
                  step={ 0.2 }
                  min={ 0.4 }
                  max={ 1 }
                  onChange={ handleScaleChange }
                  marks={ [
                    { value: 0.4, label: 'Very Long' },
                    { value: 0.6, label: 'Medium Long' },
                    { value: 0.8, label: 'Long' },
                    { value: 1, label: 'Full' },
                  ] }
                  classes={ {
                    root: sliderClasses.root,
                    thumb: sliderClasses.thumb,
                    mark: sliderClasses.mark,
                  } }
                />
              </div>
            ) }
          </div>
          <div className={ classes.createButton }>
            <Button
              onClick={ () => {
                product.images.forEach((image) => {
                  image.state = 'REMOVING_BACKGROUND';
                });
                dispatch(updateAiProduct({
                  ...product,
                }, null, () => {
                  const imageIds = product.images
                    .map((image) => image.id);
                  if (imageIds.length) {
                    dispatch(processAiProduct(product.id, {
                      tasks: ['REMOVE_BACKGROUND', 'SHADOWS'],
                      images: imageIds.map((id) => ({ imageId: id })),
                      isBeta: false,
                    }));
                  }
                }));
                setShowScaleSlider(false);
                setShowColor(false);
              } }
              disabled={ isAnyImageShadowing || isAiProductReadOnly }
            >
              <div className={ classes.buttonCreate }>
                <CreateIcon />
                Recreate
              </div>
            </Button>
          </div>
        </div>
        <div className={ classes.imagesWrapper }>
          {
            product.images.map((image) => {
              return (
                <div
                  key={ image.id }
                  onClick={ () => {
                    setIsProductImageOpen(true);
                    setActiveImage(image);
                  } }
                  className={ classes.uploadImage }
                >
                  <img src={ image.siloSrc || image.alphaSrc } />
                  {
                      image.state === 'REMOVING_BACKGROUND' && (
                      <Backdrop className={ classes.backdrop }>
                        <CircularProgress size="large" />
                        Removing Background...
                      </Backdrop>
                      )
                  }
                  {
                      image.state === 'SHADOWING' && (
                      <Backdrop className={ classes.backdrop }>
                        <CircularProgress size="large" />
                        Shadowing...
                      </Backdrop>
                      )
                  }
                </div>
              );
            })
          }
        </div>
        <div className={ classes.actions }>
          <Button
            color="secondary"
            disabled
            onClick={ () => {
              goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.EDIT(product.id)}`);
            } }
          >
            Back
          </Button>
          <Button
            onClick={ () => {
              dispatch(updateAiProduct(product, null, () => {
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.BACKGROUND(product.id)}`);
              }));
            } }
            disabled={ isAnyImageShadowing }
          >
            Next
          </Button>
        </div>
      </div>
      <ProductImageFormDialog
        Form="div"
        FormProps={ {
          className: classes.formDialog,
        } }
        open={ isProductImageOpen }
        content={ (
          <div className={ classes.imageEditorWrapper }>
            {/* <div className={ classes.imageList }>
              {
                  product.images.map((image) => {
                    return (
                      <div
                        key={ image.id }
                        onClick={ () => {
                          setActiveImage(image);
                        } }
                        className={ classNames(classes.uploadImage, { active: image === activeImage }) }
                      >
                        <img src={ image.alphaSrc } />
                      </div>
                    );
                  })
                }
            </div> */}
            <div className={ classes.productImageEditorWrapper }>
              <ProductImageEditor
                ref={ stageRef }
                image={ activeImage }
                intensity={ activeImage?.siloShadowIntensity }
                gamma={ activeImage?.siloShadowGamma }
                color={ activeImage?.siloBackgroundColor }
                mode="SILO"
              />
            </div>
            {/*             <div className={ classes.imageList }>
              <SketchPicker
                color={ activeImage?.siloBackgroundColor }
                disableAlpha
                onChange={ (value) => {
                  dispatch(renderAiProduct({
                    ...product,
                    images: product.images.map((image) => {
                      if (image.id === activeImage.id) {
                        image.siloBackgroundColor = value.rgb;
                      }
                      return image;
                    }),
                  }));
                } }
              />
              <input
                type="range"
                value={ activeImage?.siloShadowIntensity }
                min={ 0 }
                max={ 1 }
                step={ 0.1 }
                onChange={ (e) => {
                  dispatch(renderAiProduct({
                    ...product,
                    images: product.images.map((image) => {
                      if (image.id === activeImage.id) {
                        image.siloShadowIntensity = +e.target.value;
                      }
                      return image;
                    }),
                  }));
                } }
              />
              <input
                type="range"
                value={ activeImage?.siloShadowGamma }
                min={ 0.5 }
                max={ 4 }
                step={ 0.1 }
                onChange={ (e) => {
                  dispatch(renderAiProduct({
                    ...product,
                    images: product.images.map((image) => {
                      if (image.id === activeImage.id) {
                        image.siloShadowGamma = +e.target.value;
                      }
                      return image;
                    }),
                  }));
                } }
              />
            </div> */}
          </div>
        ) }
        onSave={ handleSave }
        onCancel={ handleCancel }
        onClose={ handleCancel }
        cancelButtonText="Quit"
      />
    </div>
  );
}
